import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, FolderOpen } from "lucide-react";
import { api, MONTHS } from "@/lib/api";
import { useLiveReload } from "@/lib/live";
import { useClient } from "@/context/ClientContext";
import ApprovalCard from "@/components/ApprovalCard";
import PostItAgenda from "@/components/PostItAgenda";
import PostDetailDialog from "@/components/PostDetailDialog";
import ShootSection from "@/components/ShootSection";
import ClientLogo from "@/components/ClientLogo";

const ORDER = { pending_approval: 0, changes_requested: 1, rejected: 2, approved: 3, scheduled: 4 };

export default function ClientHome() {
  const { clientId, client } = useClient();
  const [posts, setPosts] = useState([]);
  const [files, setFiles] = useState([]);
  const [open, setOpen] = useState(null);
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setDate(1); return d; });

  const load = useCallback(() => {
    api.get("/posts", { params: { client_id: clientId } }).then((r) => setPosts(r.data));
    api.get("/files", { params: { client_id: clientId } }).then((r) => setFiles(r.data));
  }, [clientId]);
  useEffect(() => { load(); }, [load]);
  useLiveReload(load);
  useEffect(() => { window.addEventListener("posts-changed", load); return () => window.removeEventListener("posts-changed", load); }, [load]);

  const byId = useMemo(() => Object.fromEntries(files.map((f) => [f.id, f])), [files]);
  const today = new Date().toISOString().slice(0, 10);
  const seen = useRef(new Set());
  const review = posts.filter((p) => p.status in ORDER && (seen.current.has(p.id) || p.status === "pending_approval" || (p.scheduled_at || "") >= today))
    .sort((a, b) => (a.scheduled_at || "").localeCompare(b.scheduled_at || ""));
  review.forEach((p) => seen.current.add(p.id));
  const pending = posts.filter((p) => p.status === "pending_approval").length;
  const move = (n) => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + n, 1));

  return (
    <div className="max-w-5xl lg:max-w-7xl pb-24 lg:pb-0">
      <div className="mb-6"><ClientLogo client={client} className="h-10 sm:h-14" fallbackClass="font-display text-2xl font-bold" testId="home-client-logo" /><p className="text-sm text-slate-400 mt-2" data-testid="home-welcome">Willkommen in eurem Portal</p></div>
      <div className="mb-8"><ShootSection /></div>
      <h1 className="text-3xl sm:text-5xl font-bold tracking-tight mb-1" data-testid="client-home-title">Zur <span className="text-teal-400">Freigabe</span></h1>
      <p className="text-sm text-slate-400 mb-5" data-testid="client-pending-text">{pending > 0 ? `${pending} ${pending === 1 ? "Beitrag wartet" : "Beiträge warten"} auf dich.` : "Alles erledigt – danke!"}</p>

      <div className="-mx-4 px-4 sm:mx-0 sm:px-0 flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 scroll-thin md:grid md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 md:gap-6 md:overflow-visible" data-testid="approval-carousel">
        {review.length === 0 && <p className="text-sm text-muted-foreground">Gerade nichts zum Freigeben.</p>}
        {review.map((p) => <ApprovalCard key={p.id} post={p} file={byId[p.media_ids?.[0]]} onOpen={setOpen} onChanged={load} />)}
      </div>

      <div className="flex items-center justify-between mt-10 mb-5">
        <h2 className="font-display text-2xl sm:text-3xl font-bold" data-testid="postit-month-title">{MONTHS[cursor.getMonth()]} <span className="text-teal-400">{cursor.getFullYear()}</span></h2>
        <div className="flex gap-2">
          <button onClick={() => move(-1)} className="h-10 w-10 grid place-items-center rounded-lg border border-border" data-testid="postit-prev"><ChevronLeft className="h-4 w-4" /></button>
          <button onClick={() => move(1)} className="h-10 w-10 grid place-items-center rounded-lg border border-border" data-testid="postit-next"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>
      <PostItAgenda cursor={cursor} posts={posts} files={files} onPostClick={setOpen} />

      <Link to="/drive" className="mt-10 md:max-w-md flex items-center justify-center gap-2 h-14 rounded-xl bg-teal-500 text-slate-950 font-semibold active:scale-[0.98] transition-transform" data-testid="client-drive-button">
        <FolderOpen className="h-5 w-5" />Zum Drive – Fotos & Videos laden
      </Link>
      {open && <PostDetailDialog post={open} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
}
